import { SOURCES } from '../../constants/map';

export const formatGeoJson = ({ data, source }) => {
	if (source === SOURCES.route) return geoJsonLocationPoints(data);

	if (source === SOURCES.earthquakes) return geoJsonRobots(data);
};

// Location Points
const formatLocationPoints = (data) => {
	if (!data) return [];

	return data?.map((point) => [point.l, point.t]);
};

const geoJsonLocationPoints = (data) => {
	const routeLine = formatLocationPoints(data);

	return {
		type: 'FeatureCollection',
		features: [
			{
				type: 'Feature',
				properties: {},
				geometry: {
					type: 'LineString',
					coordinates: routeLine
				}
			}
		]
	};
};

// Bots reporting (near-)identical locations would otherwise draw their icons on
// top of each other, so a cluster of N shows fewer than N pins. We nudge the
// *drawn* position of co-located bots apart in a small circle (~33m radius) so
// each renders as its own pin once zoomed in. The bot's real GPS (kept in
// properties.point) is untouched — only the marker's coordinates shift.
const COINCIDENT_SPREAD_RADIUS_DEG = 0.0003;

// Bots closer than this are treated as the same spot and fanned apart. Distance-
// based (not coordinate-rounding) so two bots a few metres apart can't slip into
// different buckets just by straddling a rounding boundary.
const COINCIDENT_GROUP_THRESHOLD_M = 30;

const distanceMeters = (a, b) => {
	const latRad = (Number(a.latitude) * Math.PI) / 180;
	const dLat = (Number(a.latitude) - Number(b.latitude)) * 111320;
	const dLng = (Number(a.longitude) - Number(b.longitude)) * 111320 * Math.cos(latRad);
	return Math.sqrt(dLat * dLat + dLng * dLng);
};

// Robots / earthquakes
const formatBots = (data) => {
	if (!data) return [];

	// Group bots by proximity: each bot joins the first group whose anchor is
	// within the threshold, otherwise starts a new group.
	const groups = [];
	data.forEach((point) => {
		const group = groups.find((g) => distanceMeters(g.center, point) <= COINCIDENT_GROUP_THRESHOLD_M);
		if (group) {
			group.members.push(point);
		} else {
			groups.push({
				center: { longitude: Number(point.longitude), latitude: Number(point.latitude) },
				members: [point]
			});
		}
	});

	return data.map((point) => {
		const group = groups.find((g) => g.members.includes(point));
		let longitude = Number(point.longitude);
		let latitude = Number(point.latitude);

		if (group.members.length > 1) {
			// Place every member evenly around the group's anchor point so spacing
			// is uniform. Deterministic by position in the group, so pins don't jump
			// around on the dashboard's 30s data refresh.
			const index = group.members.indexOf(point);
			const angle = (2 * Math.PI * index) / group.members.length;
			const latRad = (group.center.latitude * Math.PI) / 180;
			// Divide the lng offset by cos(lat) so the spread stays circular in metres.
			longitude = group.center.longitude + (COINCIDENT_SPREAD_RADIUS_DEG * Math.cos(angle)) / Math.max(Math.cos(latRad), 0.01);
			latitude = group.center.latitude + COINCIDENT_SPREAD_RADIUS_DEG * Math.sin(angle);
		}

		return {
			type: 'Feature',
			properties: {
				point: point,
				status: point.status,
				kyc_required: point.kyc_required
			},
			geometry: {
				type: 'Point',
				coordinates: [longitude, latitude]
			}
		};
	});
};

const geoJsonRobots = (data) => {
	const robots = formatBots(data);

	return {
		type: 'FeatureCollection',
		features: robots
	};
};

export const getBotLocationGeoJSON = (bot) => {
	if (!bot || bot.latitude == null || bot.longitude == null) {
		return {
			type: 'FeatureCollection',
			features: []
		};
	}

	return {
		type: 'FeatureCollection',
		features: [
			{
				type: 'Feature',
				properties: {
					id: bot.slug,
					name: bot.name,
					status: bot.status
				},
				geometry: {
					type: 'Point',
					coordinates: [bot.longitude, bot.latitude]
				}
			}
		]
	};
};
